/**
 * Status derivation and date formatting — the whole of Signalbox's client-side
 * logic.
 *
 * STATUS IS NEVER STORED. Only the completion stamp and the notes are facts; the
 * state of play is worked out fresh on every render from the DUE DATE. Two things
 * that gets right and a stored status does not:
 *
 *   1. Nothing has to run on a schedule for a task to turn red. There is no
 *      nightly job to fail silently.
 *   2. A row cannot hold a state that contradicts its own dates — "139 days to
 *      go" sitting next to Overdue.
 */
import type { Cadence, CycleOut, MilestoneOut, OccurrenceOut, PeriodOut, Person } from './api'

export type Status = 'complete' | 'open' | 'overdue' | 'future'

export const STATUS_LABEL: Record<Status, string> = {
  complete: 'Completed',
  open: 'To do',
  overdue: 'Overdue',
  future: 'Not started',
}

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * Parse 'YYYY-MM-DD' to LOCAL midnight. `new Date(iso)` would parse it as UTC,
 * which under British Summer Time is the previous day at 01:00 — every date in
 * the app would shift back one.
 */
export function day(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

export const startOfToday = (): Date => {
  const n = new Date()
  return new Date(n.getFullYear(), n.getMonth(), n.getDate())
}

export const fmtDate = (d: Date): string =>
  `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`

export const fmtIso = (iso: string): string => fmtDate(day(iso))

export const fmtStamp = (v: string): string => {
  const d = new Date(v)
  return `${fmtDate(d)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export const isoOf = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const daysBetween = (a: Date, b: Date): number =>
  Math.round((b.getTime() - a.getTime()) / 86400000)

/**
 * THE RULE. Four lines, and the order of the tests is the whole of it.
 *
 * The DUE DATE decides, not the period end. Several tasks fall due BEFORE their
 * period closes — "roll forward the balance sheet recs, 7 working days before
 * period end", "email Charlie re stocktake, 55 days before the year end".
 * Testing the period end first reports those as Not started for weeks after they
 * are genuinely due, which is exactly the work a chase list exists to surface.
 * Against the annual period, which runs to 28 February, a task due on 4 January
 * stayed invisible for eight weeks.
 *
 * So "Not started" means only: not yet due, in a period still running.
 */
export function deriveStatus(
  completedAt: string | null,
  due: Date,
  periodEnd: Date,
  today: Date
): Status {
  if (completedAt) return 'complete'
  if (due >= today && periodEnd >= today) return 'future'
  return due < today ? 'overdue' : 'open'
}

/**
 * On the BOARD a task in a period that has not ended is "Not started", because
 * the question there is how the period is going. MY TASKS asks a different
 * question — what should I be doing — and only the due date bears on that. A task
 * due on the 21st is due on the 21st whether or not its month has closed.
 */
export const dueStatus = (completedAt: string | null, due: Date, today: Date): Status =>
  completedAt ? 'complete' : due < today ? 'overdue' : 'open'

/**
 * The colour of a period pill, rolled up from its tasks.
 *
 * RED IS DELIBERATELY NOT "something is late". A single slipped task in an
 * otherwise healthy month would paint the whole period as failed, and once
 * everything looks failed the colour stops telling you anything. Red means there
 * is nothing left that is not late — the month has stopped moving.
 */
export function periodStatus(statuses: Status[]): Status {
  if (!statuses.length) return 'future'
  if (statuses.every((s) => s === 'future')) return 'future'
  if (statuses.every((s) => s === 'complete')) return 'complete'
  const outstanding = statuses.filter((s) => s !== 'complete')
  if (outstanding.length > 0 && outstanding.every((s) => s === 'overdue')) return 'overdue'
  return 'open'
}

// ─── Board rows ──────────────────────────────────────────────────────────────

export interface Row {
  key: string
  periodId: string
  taskId: number
  name: string
  assigneeUpn: string | null
  ref: string | null
  instructions: string | null
  group: string | null
  due: Date
  completedAt: string | null
  completedBy: string | null
  notes: OccurrenceOut['notes']
  status: Status
  hasFiles: boolean
}

export interface BoardIndex {
  byId: Map<number, { name: string; assigneeUpn: string | null; ref: string | null; instructions: string | null; taskGroup: string | null }>
  attachmentsByTask: Map<number, number>
}

/**
 * Occurrences for one period, joined to their definitions and sorted by DUE
 * DATE — the order the work has to be done in, rather than the order the tasks
 * happened to be set up in. Sorted here so every screen and roll-up sees the
 * same sequence.
 */
export function rowsFor(
  occs: OccurrenceOut[],
  period: PeriodOut,
  index: BoardIndex,
  today: Date
): Row[] {
  const periodEnd = day(period.end)
  return occs
    .filter((o) => o.periodId === period.id)
    .map((o) => {
      const def = index.byId.get(o.taskId)
      const due = day(o.due)
      return {
        key: `${o.periodId}|${o.taskId}`,
        periodId: o.periodId,
        taskId: o.taskId,
        name: def?.name ?? `Task ${o.taskId}`,
        assigneeUpn: def?.assigneeUpn ?? null,
        ref: def?.ref ?? null,
        instructions: def?.instructions ?? null,
        group: def?.taskGroup ?? null,
        due,
        completedAt: o.completedAt,
        completedBy: o.completedBy,
        notes: o.notes,
        status: deriveStatus(o.completedAt, due, periodEnd, today),
        hasFiles: (index.attachmentsByTask.get(o.taskId) ?? 0) > 0,
      }
    })
    .sort((a, b) => a.due.getTime() - b.due.getTime() || a.name.localeCompare(b.name))
}

// ─── My Tasks ────────────────────────────────────────────────────────────────

export const WINDOWS: Array<[string, string]> = [
  ['7', '7 days'],
  ['14', '14 days'],
  ['21', '21 days'],
  ['28', '28 days'],
  ['month', 'This month'],
  ['next', 'Next month'],
]

/**
 * How far ahead My Tasks looks. Without a window, adding a programme drops a
 * whole year of milestones into the list. Overdue work ignores the window and
 * always shows.
 *
 * Every option is a horizon, so widening one only ever ADDS rows. Note that
 * 'month' shrinks as the month runs out: on the 19th it reaches less far than
 * 21 days, which reads as a fault until you see the day counts beside it.
 */
export function horizonFor(window: string, today: Date): Date {
  if (window === 'month') return new Date(today.getFullYear(), today.getMonth() + 1, 0)
  if (window === 'next') return new Date(today.getFullYear(), today.getMonth() + 2, 0)
  const d = new Date(today)
  d.setDate(d.getDate() + Number(window))
  return d
}

export interface MineRow {
  key: string
  name: string
  ref: string | null
  hasFiles: boolean
  periodId: string
  periodLabel: string
  cadence: Cadence | 'programme'
  cadenceLabel: string
  due: Date
  status: Status
  notes: OccurrenceOut['notes']
  taskId?: number
  programmeTaskId?: number
}

/**
 * Everything outstanding for one person, across every cadence AND every
 * programme. Every OPEN period is scanned, not only those that have ended — the
 * status does the filtering. Filtering on the period end here would hide the
 * annual tasks due in January against a year that runs to February.
 */
export function outstandingFor(args: {
  upn: string
  periods: Record<Cadence, PeriodOut[]>
  occurrences: Record<Cadence, OccurrenceOut[]>
  index: BoardIndex
  programmes: Array<{ name: string; cycles: CycleOut[] }>
  today: Date
  window: string
}): MineRow[] {
  const { upn, periods, occurrences, index, programmes, today, window } = args
  const horizon = horizonFor(window, today)
  const rows: MineRow[] = []

  const wanted = (due: Date, completedAt: string | null): boolean =>
    !completedAt && (due < today || due <= horizon)

  for (const cadence of ['monthly', 'quarterly', 'annual'] as Cadence[]) {
    for (const period of periods[cadence].filter((p) => p.open)) {
      for (const o of occurrences[cadence].filter((x) => x.periodId === period.id)) {
        const def = index.byId.get(o.taskId)
        if ((def?.assigneeUpn ?? '') !== upn) continue
        const due = day(o.due)
        if (!wanted(due, o.completedAt)) continue
        rows.push({
          key: `${o.periodId}|${o.taskId}`,
          name: def?.name ?? `Task ${o.taskId}`,
          ref: def?.ref ?? null,
          hasFiles: (index.attachmentsByTask.get(o.taskId) ?? 0) > 0,
          periodId: period.id,
          periodLabel: periodEndLabel(cadence, period),
          cadence,
          cadenceLabel: cadence === 'annual' ? 'Annually' : cadence === 'monthly' ? 'Monthly' : 'Quarterly',
          due,
          status: dueStatus(o.completedAt, due, today),
          notes: o.notes,
          taskId: o.taskId,
        })
      }
    }
  }

  /* Programme milestones join the list on the same terms — a milestone is an
     occurrence like any other, and My Tasks is the one place both kinds of work
     have to appear together. */
  for (const prog of programmes) {
    for (const cycle of prog.cycles.filter((c) => c.open)) {
      for (const m of cycle.milestones) {
        if ((m.assigneeUpn ?? '') !== upn) continue
        const due = day(m.due)
        if (!wanted(due, m.completedAt)) continue
        rows.push({
          key: `${cycle.periodId}|${m.programmeTaskId}`,
          name: m.name,
          ref: m.ref,
          hasFiles: false,
          periodId: cycle.periodId,
          periodLabel: cycle.label,
          cadence: 'programme',
          cadenceLabel: prog.name,
          due,
          status: dueStatus(m.completedAt, due, today),
          notes: m.notes,
          programmeTaskId: m.programmeTaskId,
        })
      }
    }
  }

  return rows.sort((a, b) => a.due.getTime() - b.due.getTime() || a.name.localeCompare(b.name))
}

/** A month and a quarter are identified by the day they close; a year by the month. */
export const periodEndLabel = (cadence: Cadence, p: PeriodOut): string => {
  const end = day(p.end)
  return cadence === 'annual'
    ? `${MONTHS[end.getMonth()]} ${end.getFullYear()}`
    : fmtDate(end)
}

// ─── People ──────────────────────────────────────────────────────────────────

/** Initials, derived rather than stored — the app holds names, not initials. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function nameFor(people: Person[], upn: string | null): string {
  if (!upn) return 'Unassigned'
  const p = people.find((x) => x.upn === upn)
  return p?.displayName ?? upn
}

export const dueLabel = (due: number, dueType: string): string => {
  const unit = dueType === 'calendar' ? 'calendar day' : 'working day'
  if (due === 0) return 'on the period end'
  const s = Math.abs(due) === 1 ? '' : 's'
  return due < 0
    ? `${Math.abs(due)} ${unit}${s} before period end`
    : `${due} ${unit}${s} after period end`
}

export const milestoneStatus = (m: MilestoneOut, today: Date): Status =>
  dueStatus(m.completedAt, day(m.due), today)
